import { Colors, Icon, Typography } from '@amelio/ui-library';

import {
    TABLE_CALIBRATED_SCORE_COLUMN_WIDTH,
    TABLE_COMMENT_COLUMN_WIDTH,
    TABLE_HEADER_CELL,
    TABLE_LEVEL_COLUMN_WIDTH,
    TABLE_MANAGER_COLUMN_WIDTH,
    TABLE_PERSON_COLUMN_WIDTH,
    TABLE_SCORE_COLUMN_WIDTH,
    TABLE_SORT_BUTTON,
} from '../calibrationStyles';

import type { IconProps } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';

export type SortColumn = 'person' | 'manager' | 'score' | 'calibratedScore';
export type SortDirection = 'asc' | 'desc';

const SORT_ICON_NAME: Record<SortDirection, IconProps['name']> = {
    asc: 'arrow-arrow-up',
    desc: 'arrow-arrow-down',
};

/** `aria-sort` only ever describes the one column currently sorted. */
const ARIA_SORT: Record<SortDirection, 'ascending' | 'descending'> = {
    asc: 'ascending',
    desc: 'descending',
};

interface HeaderLabelProps {
    label: string;
    isActive?: boolean;
}

/** Column caption: small caps, widely tracked, grey until its column is the one being sorted. */
const HeaderLabel = ({ label, isActive = false }: HeaderLabelProps) => (
    <Typography
        variant={'overline'}
        color={isActive ? Colors.NAVY : Colors.BASE_400}
        as={'span'}
    >
        {label}
    </Typography>
);

interface HeaderCellProps {
    label: string;
    widthClassName: string;
}

const HeaderCell = ({ label, widthClassName }: HeaderCellProps) => (
    <th className={`${TABLE_HEADER_CELL} ${widthClassName}`}>
        <HeaderLabel label={label} />
    </th>
);

interface SortableHeaderCellProps extends HeaderCellProps {
    column: SortColumn;
    activeColumn: SortColumn | null;
    direction: SortDirection;
    onSort: (column: SortColumn) => void;
}

const SortableHeaderCell = ({
    column,
    label,
    widthClassName,
    activeColumn,
    direction,
    onSort,
}: SortableHeaderCellProps) => {
    const isActive = activeColumn === column;

    return (
        <th
            className={`${TABLE_HEADER_CELL} ${widthClassName}`}
            aria-sort={isActive ? ARIA_SORT[direction] : undefined}
        >
            <button
                type={'button'}
                onClick={() => onSort(column)}
                className={TABLE_SORT_BUTTON}
                data-testid={`calibration-table-sort-${column}`}
            >
                <HeaderLabel
                    label={label}
                    isActive={isActive}
                />

                {/* Shown on every sortable column, not only the sorted one: the icon is what
                    tells a reader the caption is a control at all. */}
                <Icon
                    name={isActive ? SORT_ICON_NAME[direction] : 'essential-sort'}
                    size={'xs'}
                    color={isActive ? Colors.PRIMARY : Colors.BASE_300}
                    ariaHidden
                />
            </button>
        </th>
    );
};

export interface EmployeeTableHeaderProps {
    activeColumn: SortColumn | null;
    direction: SortDirection;
    onSort: (column: SortColumn) => void;
}

/** The employee table's seven column headers: four sortable, three plain, all carrying the
 * column width every row lines up on. */
export const EmployeeTableHeader = ({
    activeColumn,
    direction,
    onSort,
}: EmployeeTableHeaderProps) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabelCapitalized } = useCompanyInformation();

    const sortableProps = { activeColumn, direction, onSort };

    return (
        <thead>
            <tr className={'border-base-100 border-b'}>
                <SortableHeaderCell
                    column={'person'}
                    label={t('calibration.table.columns.person')}
                    widthClassName={TABLE_PERSON_COLUMN_WIDTH}
                    {...sortableProps}
                />

                <SortableHeaderCell
                    column={'manager'}
                    label={t('calibration.table.columns.manager', {
                        managerLabel: getManagerLabelCapitalized(),
                    })}
                    widthClassName={TABLE_MANAGER_COLUMN_WIDTH}
                    {...sortableProps}
                />

                <SortableHeaderCell
                    column={'score'}
                    label={t('calibration.table.columns.roleAttainment')}
                    widthClassName={TABLE_SCORE_COLUMN_WIDTH}
                    {...sortableProps}
                />

                <HeaderCell
                    label={t('calibration.table.columns.givenLevel')}
                    widthClassName={TABLE_LEVEL_COLUMN_WIDTH}
                />

                <SortableHeaderCell
                    column={'calibratedScore'}
                    label={t('calibration.table.columns.calibratedScore')}
                    widthClassName={TABLE_CALIBRATED_SCORE_COLUMN_WIDTH}
                    {...sortableProps}
                />

                <HeaderCell
                    label={t('calibration.table.columns.calibratedLevel')}
                    widthClassName={TABLE_LEVEL_COLUMN_WIDTH}
                />

                <HeaderCell
                    label={t('calibration.table.columns.comment')}
                    widthClassName={TABLE_COMMENT_COLUMN_WIDTH}
                />
            </tr>
        </thead>
    );
};
